import { h, defineComponent, ref, computed, VNode } from 'vue';
import { toUpperCaseExceptSpecialChars } from '@/ui/helpers';
import { Tag, TagType } from '@/ui/components/Tag';
import { Icon } from '@/ui/components/icon/Icon';
import { useApproval } from '@/ui/concerns/approval/useApproval';
import { ApprovalStatus } from '@/entities/approvals/constants';
import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
import { t } from '@/ui/i18n/i18n';
import { reset as resetApprovalAction } from '@/services/api/resources/approval';
import { VNodeData } from 'vue/types/vnode';
import { approvalStatusToText } from '@/ui/dictionary';

export interface PageTitleWithTagProps {
  text: string;
  classes?: string;
  status?: string;
  approvalId?: number;
}

export const PageTitleWithTag = defineComponent({
  name: 'PageTitleWithTag',
  props: {
    text: {
      type: String,
      required: false,
    },
    classes: {
      type: String,
      required: false,
    },
    status: {
      type: String,
      required: false,
    },
    approvalId: {
      type: Number,
      required: false,
    },
  },
  setup(props, context) {
    const isResetApprovalModalOpen = ref(false);

    const openResetApprovalModal = () => {
      isResetApprovalModalOpen.value = true;
    };

    const closeResetApprovalModal = () => {
      isResetApprovalModalOpen.value = false;
    };

    const resetApproval = () => {
      resetApprovalTask();
      closeResetApprovalModal();
    };

    const { approvalStatusToTagType } = useApproval();

    const getType = (): TagType => {
      const status = (props.status ?? ApprovalStatus.Pending) as keyof typeof approvalStatusToTagType;
      return approvalStatusToTagType[status];
    };

    const canResetApproval = computed(() => props.status !== ApprovalStatus.Pending);

    const resetApprovalTask = async () => {
      if (props.approvalId) {
        isResetApprovalModalOpen.value = false;
        await resetApprovalAction(props.approvalId)();
        context.emit('reset:approval');
      }
    };

    return function render(): VNode {
      return h(
        'div',
        { class: 'title-wrapper', style: { display: 'flex', alignItems: 'center', gap: '0.5rem' } },
        [
          h(ConfirmationModal, {
            class: 'reset-key-confirmation-modal',
            props: {
              active: isResetApprovalModalOpen.value,
              'confirm-text': t('approve.modal.reset.confirm'),
              'decline-text': t('approve.modal.reset.decline'),
              confirmationButtonProps: { type: 'is-info', loading: false },
            },
            on: {
              declined: closeResetApprovalModal,
              accepted: resetApproval,
            },
          }, [
            h('h1', t('approve.modal.reset.title')),
            h('div', t('approve.modal.reset.content')),
          ]),
          h(
            'h1',
            { class: `title ${props.classes ?? 'is-3'}`, style: { margin: 0 } },
            props.text
              ? toUpperCaseExceptSpecialChars(props.text)
              : null,
          ),
          props.approvalId !== undefined && h(Tag, {
            props: { type: getType() },
            class: 'tag is-info',
            style: {
              display: 'flex',
              'flex-direction': 'row',
              'align-items': 'center',
              'justify-content': 'space-between',
              gap: '0.5rem',
              'font-size': '0.8rem',
            },
          },
          [approvalStatusToText(props.status as ApprovalStatus),
            canResetApproval.value && h(Icon, {
              style: {
                cursor: 'pointer',
                'margin-left': '0.5rem',
                'font-size': '0.8rem',
              },
              props: {
                'icon-pack': 'far',
                icon: 'times',
              },
              on: { click: openResetApprovalModal },
            })])],
      );
    };
  },
});

export function pageTitleWithTag(data: VNodeData & { props: PageTitleWithTagProps }) {
  return h(PageTitleWithTag, { ...data });
}
